import { getSelectStyles, getActionSelectStyles } from '../utility/selectStyles';
import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {FaPlus, FaSyncAlt, FaTrash, FaDatabase} from 'react-icons/fa';
import {AppDispatch, RootState} from '../store/store';
import {useToast} from '../components/ToastContext';
import Modal from '../components/Modal';
import axiosInstance from '../utility/axiosInstance';
import {handleAxiosError} from '../helpers/handleAxiosError';
import {fetchVolumes} from '../store/slices/volumesSlice';
import Select, {SingleValue} from 'react-select';
import {fetchBackups, setBackupStatus} from '../store/slices/backupsSlice';
import {useConfirm} from "../components/ConfirmContext";
import TrialLock, {useResourceCreationLock} from '../components/TrialLock';
import { Link } from 'react-router-dom';
import { formatConsoleDate } from '../utility/formatConsoleDate';
import useStorageEstimate from '../hooks/useStorageEstimate';

const ITEMS_PER_PAGE = 20;
const HOURS_PER_MONTH = 730;

const BackupsPage: React.FC = () => {
    const {confirm} = useConfirm();
    const {showToast} = useToast();
    const dispatch = useDispatch<AppDispatch>();
    const volumes = useSelector((state: RootState) => state.volumes);
    const backups = useSelector((state: RootState) => state.backups);
    const instances = useSelector((state: RootState) => state.instances.data);
    const billingForecast = useSelector((state: RootState) => state.billingForecast.forecast);

    const creationLocked = useResourceCreationLock();
    const [loading, setLoading] = useState(false);
    const [showCreateBackup, setShowCreateBackup] = useState(false);
    const [selectedVolume, setSelectedVolume] = useState<string>('');
    const [backupName, setBackupName] = useState<string>('');

    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [selectedBackup, setSelectedBackup] = useState<any>(null);
    const [restoreVolumeId, setRestoreVolumeId] = useState<string>('');
    const [restoreVolumeName, setRestoreVolumeName] = useState<string>('');
    const [restoreMode, setRestoreMode] = useState<'existing' | 'new'>('new');
    const [currentPage, setCurrentPage] = useState(0);

    useEffect(() => {
        setCurrentPage(0);
    }, [backups.backups.length]);

    const sortedBackups = [...backups.backups].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const totalBackups = sortedBackups.length;
    const totalPages = Math.ceil(totalBackups / ITEMS_PER_PAGE);
    const paginatedBackups = sortedBackups.slice(
        currentPage * ITEMS_PER_PAGE,
        (currentPage + 1) * ITEMS_PER_PAGE
    );

    const getVolumeLabel = (volumeId: string): string => {
        if (!volumeId) return '';
        const vol = volumes.data.find((v: any) => v.id === volumeId);
        if (!vol) return `Volume ${volumeId}`;

        let label = `${vol.name || 'Boot Volume'} (${vol.size} GB)`;
        if (vol.attachments && vol.attachments.length > 0) {
            const instanceId = vol.attachments[0].server_id;
            const instance = instances.find((inst: any) => inst.id === instanceId);
            const instanceName = instance ? instance.name : 'Unknown Instance';
            label += ` - Attached to ${instanceName}`;
        }
        return label;
    };

    const getInstanceNameForBackup = (volumeId: string): string => {
        if (!volumeId) return '-';
        const vol = volumes.data.find((v: any) => v.id === volumeId);
        if (!vol) return '-';
        if (vol.attachments && vol.attachments.length > 0) {
            const instance = instances.find((inst: any) => inst.id === vol.attachments[0].server_id);
            if (instance) return instance.name;
        }
        return vol.name || 'Boot Volume';
    };

    const getSelectedVolumeLabel = () => getVolumeLabel(selectedVolume);
    const selectedBackupVolume = volumes.data.find((volume: any) => String(volume.id) === selectedVolume);
    const backupEstimate = useStorageEstimate('backup', Number(selectedBackupVolume?.size) || null);
    const getCurrentHourlyRate = (resourceId: string): number | null => {
        const item = billingForecast?.active.find((resource) =>
            resource.service === 'backup' && String(resource.os_resource_id) === String(resourceId)
        );
        const rate = Number(item?.current_hourly_rate);
        return Number.isFinite(rate) ? rate : null;
    };
    const renderCurrentRate = (resourceId: string) => {
        const rate = getCurrentHourlyRate(resourceId);
        if (rate === null) return null;
        const hourly = rate.toFixed(rate > 0 && rate < 0.01 ? 5 : 2);
        return (
            <small style={{ display: 'block', marginTop: 2, color: 'var(--text-muted)' }} title="Current hourly rate before credits">
                Current: £{hourly}/hr · ~£{(rate * HOURS_PER_MONTH).toFixed(2)}/mo
            </small>
        );
    };
    const eligibleRestoreVolumes = volumes.data.filter((vol: any) =>
        vol.status === 'available' && Number(vol.size) >= Number(selectedBackup?.size || 0)
    );

    const closeRestoreModal = () => {
        setShowRestoreModal(false);
        setRestoreVolumeId('');
        setRestoreVolumeName('');
        setRestoreMode('new');
        setSelectedBackup(null);
    };

    const renderEmptyBackupState = () => (
        <div className="resource-empty-state">
            <strong>No backups yet</strong>
            <span>Create a point-in-time copy of a volume, then restore it to a new or available volume when needed.</span>
            <span className="resource-empty-state__actions">
                <Link to="/category/block-storage/volumes">View volumes</Link>
            </span>
        </div>
    );


    const handleCreateBackup = async (e: React.FormEvent) => {
        e.preventDefault();
        if (creationLocked || !selectedVolume || !backupName.trim()) return;

        await executeCreateBackup(selectedVolume, backupName.trim());
    };

    const executeCreateBackup = async (volumeId: string, name: string) => {
        setLoading(true);
        setShowCreateBackup(false);
        try {
            const payload = {
                volumeId,
                backupName: name,
            };

            const response = await axiosInstance.post('/v1/backup', payload);
            if (response.status === 200) {
                dispatch(fetchBackups());
                setShowCreateBackup(false);
                setBackupName('');
                setSelectedVolume('');
            }
        } catch (err) {
            console.error('Error creating backup:', err);
            const message = handleAxiosError(err);
            showToast(message || 'Failed to create backup', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteBackup = async (backupId: string, backupName: string) => {
        const proceed = await confirm({
            title: 'Delete Backup',
            message: `Delete backup "${backupName}"? It cannot be used for recovery after deletion.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
        });
        if (!proceed) return;
        await executeDeleteBackup(backupId, backupName);
    };

    const executeDeleteBackup = async (backupId: string, name: string) => {
        dispatch(setBackupStatus({id: backupId, status: 'deleting'}));

        setLoading(true);
        try {
            await axiosInstance.delete(`/v1/backup/${backupId}`);
            showToast(`Backup "${name}" deleted`, 'success');
            dispatch(fetchBackups());
        } catch (err) {
            console.error('Delete backup error:', err);
            handleAxiosError(err);
            showToast(`Failed to delete backup "${name}"`, 'error');
            dispatch(setBackupStatus({id: backupId, status: 'available'}));
        } finally {
            setLoading(false);
        }
    };

    const handleRestoreBackup = (backup: any) => {
        setSelectedBackup(backup);
        setRestoreMode('new');
        setRestoreVolumeId('');
        setRestoreVolumeName('');
        setShowRestoreModal(true);
    };

    const executeRestoreBackup = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedBackup) return;
        if (restoreMode === 'existing' && !restoreVolumeId) return;
        if (restoreMode === 'new' && !restoreVolumeName.trim()) return;

        if (restoreMode === 'existing') {
            const targetVolume = getVolumeLabel(restoreVolumeId);
            const proceed = await confirm({
                title: 'Overwrite existing volume?',
                message: `Restore "${selectedBackup.name}" to "${targetVolume}"? This permanently replaces the target volume's current data.`,
                confirmText: 'Overwrite & Restore',
                cancelText: 'Cancel',
                type: 'danger',
            });
            if (!proceed) return;
        }

        setShowRestoreModal(false);
        setLoading(true);
        try {
            const payload: any = {};
            if (restoreMode === 'existing') {
                payload.volumeId = restoreVolumeId;
            } else {
                payload.volumeName = restoreVolumeName.trim();
            }

            const response = await axiosInstance.post(`/v1/backup/${selectedBackup.id}/restore`, payload);
            if (response.status === 200) {
                showToast(`Restoration of "${selectedBackup.name}" started`, 'success');
                dispatch(fetchBackups());
                closeRestoreModal();
                await dispatch(fetchVolumes());
            }
        } catch (err) {
            console.error('Error restoring backup:', err);
            handleAxiosError(err);
            showToast('Failed to restore backup', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="keypairs-page">
            <div className="page-header">
                <h1 className="page-title">Backups</h1>
                <p className="page-subtitle">
                    Point-in-time copies of a volume.
                </p>
                <a href="https://docs.redu.cloud/Guides/backups-guide" target="_blank" rel="noopener noreferrer"
                   className="docs-link">
                    Documentation →
                </a>
            </div>

            <div className="desktop-view">
                <div className="table-container">
                    <table className="keypairs-table" aria-label="Volume backups">
                        <thead hidden={backups.status !== 'loading' && paginatedBackups.length === 0}>
                        <tr>
                            <th>Backup Name</th>
                            <th>Instance</th>
                            <th>Size (GB)</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {backups.status === 'loading' ? (
                            <tr>
                                <td colSpan={6} className="loading-state">
                                    <FaSyncAlt className="spin"/> Loading backups...
                                </td>
                            </tr>
                        ) : backups.status === 'failed' ? (
                            <tr>
                                <td colSpan={6} className="error-state">
                                    Error: {backups.error || 'Failed to load backups'}
                                </td>
                            </tr>
                        ) : paginatedBackups.length > 0 ? (
                            paginatedBackups.map((backup: any) => (
                                <tr key={backup.id}>
                                    <td className="keypair-name">{backup.name}</td>
                                    <td>{getInstanceNameForBackup(backup.volume_id)}</td>
                                    <td>
                                        {backup.size} GB
                                        {renderCurrentRate(backup.id)}
                                    </td>
                                    <td>

                                        {backup.status === 'deleting' || backup.status === 'error_deleting' ? (
                                            <div>
                                                <div className="deleting-progress"></div>
                                                <div className="status-label" style={{color: '#ef4444'}}>DELETING</div>
                                            </div>
                                        ) : backup.status === 'creating' || backup.status === 'restoring' ? (
                                            <div>
                                                <div className="creating-progress"></div>
                                                <div className="status-label" style={{color: 'forestgreen'}}>
                                                    {backup.status === 'restoring' ? 'RESTORING' : 'CREATING'}
                                                </div>
                                            </div>
                                        ) : (
                                            <span
                                                className={backup.status === 'available' ? 'text-success' : 'text-warning'}>
              {backup.status}
            </span>
                                        )}
                                    </td>
                                    <td>{formatConsoleDate(backup.created_at)}</td>
                                    <td>
                                        <Select
                                            options={[
                                                {value: 'restore', label: 'Restore to Volume'},
                                                {value: 'delete', label: 'Delete Backup'}
                                            ]}
                                            onChange={(opt) => {
                                                if (opt?.value === 'delete') handleDeleteBackup(backup.id, backup.name);
                                                if (opt?.value === 'restore') handleRestoreBackup(backup);
                                            }}
                                            placeholder="Actions"
                                            isSearchable={false}
                                            aria-label={`Actions for backup ${backup.name}`}
                                            menuPortalTarget={document.body}
                                            styles={getActionSelectStyles()}
                                        />
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={6} className="empty-state">
                                    {renderEmptyBackupState()}
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px', borderTop: '1px solid var(--border)' }}>
                            <button
                                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                disabled={currentPage === 0}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                            >← Prev</button>
                            <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalBackups)} of {totalBackups}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={currentPage >= totalPages - 1}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                            >Next →</button>
                        </div>
                    )}
                </div>
            </div>


            <div className="mobile-view">
                {paginatedBackups.length > 0 ? (
                    paginatedBackups.map((backup: any) => (
                        <div key={backup.id} className="keypair-card">
                            <div className="card-header">
                                <h3 className="card-title">{backup.name}</h3>
                                <Select
                                    options={[
                                        {value: 'restore', label: 'Restore to Volume'},
                                        {value: 'delete', label: 'Delete Backup'}
                                    ]}
                                    onChange={(opt) => {
                                        if (opt?.value === 'delete') handleDeleteBackup(backup.id, backup.name);
                                        if (opt?.value === 'restore') handleRestoreBackup(backup);
                                    }}
                                    placeholder="Actions"
                                    isSearchable={false}
                                    aria-label={`Actions for backup ${backup.name}`}
                                    menuPortalTarget={document.body}
                                    styles={getActionSelectStyles()}
                                />
                            </div>
                            <div className="card-content">
                                <div className="fingerprint">
                                    <span className="label">Instance:</span>
                                    <span className="value">{getInstanceNameForBackup(backup.volume_id)}</span>
                                </div>
                                <div className="fingerprint">
                                    <span className="label">Size:</span>
                                    <span className="value">
                                        {backup.size} GB
                                        {renderCurrentRate(backup.id)}
                                    </span>
                                </div>

                                <div className="fingerprint">
                                    <span className="label">Status:</span>
                                    <span className="value">
              {backup.status === 'deleting' || backup.status === 'error_deleting' ? (
                  <div>
                      <div className="deleting-progress"></div>
                      <div className="status-label" style={{color: '#ef4444'}}>DELETING</div>
                  </div>
              ) : backup.status === 'creating' || backup.status === 'restoring' ? (
                  <div>
                      <div className="creating-progress"></div>
                      <div className="status-label" style={{color: 'forestgreen'}}>
                          {backup.status === 'restoring' ? 'RESTORING' : 'CREATING'}
                      </div>
                  </div>
              ) : (
                  <span className={backup.status === 'available' ? 'text-success' : 'text-warning'}>
                  {backup.status}
                </span>
              )}
            </span>
                                </div>
                                <div className="fingerprint">
                                    <span className="label">Created:</span>
                                    <span className="value">{formatConsoleDate(backup.created_at)}</span>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="empty-state">
                        {renderEmptyBackupState()}
                    </div>
                )}
                {totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px' }}>
                        <button
                            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                            disabled={currentPage === 0}
                            style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                        >← Prev</button>
                        <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                            {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalBackups)} of {totalBackups}
                        </span>
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={currentPage >= totalPages - 1}
                            style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                        >Next →</button>
                    </div>
                )}
            </div>


            <TrialLock>
                <button
                    className="create-button"
                    onClick={() => setShowCreateBackup(true)}
                    title="Create New Backup"
                >
                    <FaDatabase/>
                    <span>Create Backup</span>
                </button>
            </TrialLock>


            <Modal
                isOpen={showCreateBackup && !creationLocked}
                onClose={() => {
                    setShowCreateBackup(false);
                    setBackupName('');
                    setSelectedVolume('');
                }}
                title="Create Backup"
            >
                <form onSubmit={handleCreateBackup} className="create-form">
                    <div className="form-group">
                        <label htmlFor="backup-volume">Volume</label>
                        <Select
                            inputId="backup-volume"
                            options={volumes.data.map((vol: any) => {
                                let label = `${vol.name || 'Boot Volume'} (${vol.size} GB)`;
                                if (vol.attachments && vol.attachments.length > 0) {
                                    const instanceId = vol.attachments[0].server_id;
                                    const instance = instances.find((inst: any) => inst.id === instanceId);
                                    const instanceName = instance ? instance.name : 'Unknown Instance';
                                    label += ` - Attached to ${instanceName}`;
                                }
                                return {
                                    value: String(vol.id),
                                    label: label,
                                };
                            })}
                            value={selectedVolume ? {value: selectedVolume, label: getSelectedVolumeLabel()} : null}
                            onChange={(opt: SingleValue<{ value: string; label: string }>) =>
                                setSelectedVolume(opt?.value || '')
                            }
                            placeholder="Select a volume"
                            isSearchable={true}
                            menuPortalTarget={document.body}
                            styles={getActionSelectStyles()}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="backup-name">Backup Name</label>
                        <input
                            id="backup-name"
                            type="text"
                            value={backupName}
                            onChange={(e) => setBackupName(e.target.value)}
                            required
                            placeholder="e.g., db-backup-june"
                            autoFocus
                        />
                    </div>

                    <p className="help-text">
                        This creates a manual backup of volume data, not a virtual-machine image. It is retained until you delete it and billed for the storage it uses. The backup becomes restorable after its status changes to Available.{' '}
                        <a href="https://docs.redu.cloud/Guides/backups-guide" target="_blank"
                           rel="noopener noreferrer">
                            Learn more
                        </a>
                    </p>
                    <p className="help-text">
                        Backup storage is billed hourly by its stored GB for as long as it is retained.
                        {selectedBackupVolume && <> The selected volume is <strong>{selectedBackupVolume.size} GB</strong>.</>}
                        {backupEstimate.status === 'loading' && <> Calculating the estimate…</>}
                        {backupEstimate.status === 'ready' && <>
                            {' '}Estimated at <strong>£{backupEstimate.estimate.hourly_total.toFixed(5)}/hr · ~£{backupEstimate.estimate.monthly_total.toFixed(2)}/mo</strong>
                            {' '}for {backupEstimate.estimate.size_gb} GB at £{backupEstimate.estimate.unit_hourly_rate.toFixed(5)}/GB/hr, before credits.
                        </>}
                        {backupEstimate.status === 'unavailable' && <> Pricing for backup storage is not configured yet. <Link to="/category/user/billing">Billing</Link> remains the authoritative record for rates, usage, and credits.</>}
                    </p>

                    <div className="modal-actions">
                        <button type="button" className="cancel-button" onClick={() => setShowCreateBackup(false)}>
                            Cancel
                        </button>
                        <button
                            disabled={loading || !selectedVolume || !backupName.trim()}
                            className="submit-button"
                            type="submit"
                        >
                            {loading ? 'Creating...' : 'Create Backup'}
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal
                isOpen={showRestoreModal && !creationLocked}
                onClose={closeRestoreModal}
                title={`Restore Backup: ${selectedBackup?.name}`}
            >
                <form onSubmit={executeRestoreBackup} className="create-form">
                    <p className="help-text">
                        Restoring keeps the backup intact. Create a new volume for the safest recovery path; restoring to an existing volume replaces its current data.
                    </p>
                    <div className="form-group">
                        <label id="restore-mode-label">Restore To</label>
                        <div role="radiogroup" aria-labelledby="restore-mode-label" style={{display: 'flex', gap: '20px', marginBottom: '10px'}}>
                            <label htmlFor="restore-existing-volume" style={{display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'normal'}}>
                                <input
                                    id="restore-existing-volume"
                                    type="radio"
                                    name="restoreMode"
                                    value="existing"
                                    checked={restoreMode === 'existing'}
                                    onChange={() => setRestoreMode('existing')}
                                />
                                Existing volume (overwrites data)
                            </label>
                            <label htmlFor="restore-new-volume" style={{display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'normal'}}>
                                <input
                                    id="restore-new-volume"
                                    type="radio"
                                    name="restoreMode"
                                    value="new"
                                    checked={restoreMode === 'new'}
                                    onChange={() => setRestoreMode('new')}
                                />
                                New volume (recommended)
                            </label>
                        </div>
                    </div>

                    {restoreMode === 'existing' ? (
                        <div className="form-group">
                            <label htmlFor="restore-target-volume">Target Volume</label>
                            {eligibleRestoreVolumes.length > 0 ? (
                                <Select
                                    inputId="restore-target-volume"
                                    options={eligibleRestoreVolumes.map((vol: any) => ({
                                        value: String(vol.id),
                                        label: getVolumeLabel(vol.id),
                                    }))}
                                    value={restoreVolumeId ? {
                                        value: restoreVolumeId,
                                        label: getVolumeLabel(restoreVolumeId)
                                    } : null}
                                    onChange={(opt: SingleValue<{ value: string; label: string }>) =>
                                        setRestoreVolumeId(opt?.value || '')
                                    }
                                    placeholder="Select target volume"
                                    isSearchable={true}
                                    required
                                />
                            ) : (
                                <p className="help-text">
                                    No available volume is large enough for this {selectedBackup?.size} GB backup. Create a new volume instead.
                                </p>
                            )}
                            <p className="help-text" style={{marginTop: '10px'}}>
                                Restoring to an existing volume overwrites all of its data. It must be available and at least {selectedBackup?.size} GB.
                            </p>
                        </div>
                    ) : (
                        <div className="form-group">
                            <label htmlFor="restore-new-volume-name">New Volume Name</label>
                            <input
                                id="restore-new-volume-name"
                                type="text"
                                value={restoreVolumeName}
                                onChange={(e) => setRestoreVolumeName(e.target.value)}
                                required
                                placeholder="e.g., restored-volume"
                            />
                            <p className="help-text" style={{marginTop: '10px'}}>
                                A new volume will be created with the specified name and the same size as the backup ({selectedBackup?.size} GB).
                            </p>
                        </div>
                    )}

                    <div className="modal-actions">
                        <button type="button" className="cancel-button" onClick={closeRestoreModal}>
                            Cancel
                        </button>
                        <button
                            disabled={loading || (restoreMode === 'existing' ? !restoreVolumeId : !restoreVolumeName.trim())}
                            className="submit-button"
                            type="submit"
                        >
                            {loading ? 'Restoring...' : restoreMode === 'existing' ? 'Restore to Volume' : 'Create & Restore'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default BackupsPage;
